import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { DentalPhotoAlbum } from '@/shared/api/protocol-ts/model/dto_dental_photo_album_pb';
import {
  DefaultInitialState,
  LoadingStateType,
  SliceName,
} from '@/shared/config';

import type { PersistedPhotoTransform } from '../config';

import {
  deleteAlbum,
  movePhotosToAlbum,
  reorderAlbumAssets,
} from './albumSlice.thunks';

// NOTE: Time - the date manually specified by the user, Created - the album creation date
const sortAlbums = (a: DentalPhotoAlbum, b: DentalPhotoAlbum) => {
  const aCreatedAt =
    a.Time?.toDate().getTime() ?? a.Created?.At?.toDate().getTime() ?? 0;
  const bCreatedAt =
    b.Time?.toDate().getTime() ?? b.Created?.At?.toDate().getTime() ?? 0;

  return bCreatedAt - aCreatedAt;
};

export const albumAdapter = createEntityAdapter<DentalPhotoAlbum>({
  selectId: (album) => album.ID,
  sortComparer: sortAlbums,
});

type AlbumSliceInitialState = DefaultInitialState & {
  isDeleteAssetPending: boolean;
  photoTransforms: Record<string, PersistedPhotoTransform>;
};

const initialStateFlags = {
  isDeleteAssetPending: false,
  photoTransforms: {} as Record<string, PersistedPhotoTransform>,
};

const initialState: AlbumSliceInitialState = {
  loading: 'idle',
  ...initialStateFlags,
};

const albumState = albumAdapter.getInitialState(initialState);

type AlbumState = typeof albumState;

const albumSlice = createSlice({
  name: SliceName.album,
  initialState: albumAdapter.getInitialState(initialState),
  reducers: {
    addOne: (state, action: PayloadAction<DentalPhotoAlbum>) => {
      albumAdapter.addOne(state as AlbumState, action.payload);
    },
    addMany: (state, action: PayloadAction<DentalPhotoAlbum[]>) => {
      albumAdapter.addMany(state as AlbumState, action.payload);
    },
    setMany: (state, action: PayloadAction<DentalPhotoAlbum[]>) => {
      albumAdapter.setMany(state as AlbumState, action.payload);
    },
    setNewestOne: (state, action: PayloadAction<DentalPhotoAlbum>) => {
      const currentRevisionNumber =
        state.entities[action.payload.ID]?.Revision?.Number ?? 0;
      const payloadRevisionNumber = action.payload.Revision?.Number ?? 0;

      if (
        payloadRevisionNumber > currentRevisionNumber &&
        !action.payload.Deleted?.Deleted
      ) {
        albumAdapter.setOne(state as AlbumState, action.payload);
      }
    },
    removeOne: (state, action: PayloadAction<string>) => {
      albumAdapter.removeOne(state as AlbumState, action.payload);
    },
    removeAll: (state) => {
      albumAdapter.removeAll(state as AlbumState);
    },
    setLoading: (state, action: PayloadAction<LoadingStateType>) => {
      state.loading = action.payload;
    },
    setPhotoTransform: (
      state,
      action: PayloadAction<{
        photoID: string;
        transform: PersistedPhotoTransform;
      }>,
    ) => {
      state.photoTransforms[action.payload.photoID] = action.payload.transform;
    },
    clearPhotoTransform: (state, action: PayloadAction<string>) => {
      delete state.photoTransforms[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(deleteAlbum.fulfilled, (state, action) => {
      const { DeletedAlbumID } = action.payload;

      if (DeletedAlbumID) {
        albumAdapter.removeOne(state as AlbumState, DeletedAlbumID);
      }
    });

    builder.addCase(reorderAlbumAssets.fulfilled, (state, action) => {
      const { DentalPhotoAlbum } = action.payload;

      if (DentalPhotoAlbum) {
        albumAdapter.setOne(state as AlbumState, DentalPhotoAlbum);
      }
    });

    builder.addCase(movePhotosToAlbum.fulfilled, (state, action) => {
      const { DentalPhotoAlbums } = action.payload;

      if (DentalPhotoAlbums && DentalPhotoAlbums.length > 0) {
        albumAdapter.setMany(state as AlbumState, DentalPhotoAlbums);
      }
    });
  },
});

export const { actions } = albumSlice;

export default albumSlice.reducer;
